import React, { useEffect, useState } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";

const COLLECTION_VALORES = "financeiro_valores_servicos";
const TIPOS_EQUIPAMENTO = [
  "Pin Load",
  "Plate Load",
  "Banco",
  "Cross",
  "Guiado",
  "Livre",
  "Esteira",
  "Bike",
  "Spinning",
  "Eliptico",
  "Escada",
  "Remo",
];

function buildDefaultMap() {
  const base = {};
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    base[tipo] = 0;
  });
  return base;
}

function parseNumero(value) {
  const n = Number.parseFloat(String(value ?? "0").replace(",", "."));
  return Number.isNaN(n) ? 0 : n;
}

export default function FinanceiroValoresServicosPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();

  const [valoresInstalacao, setValoresInstalacao] = useState(() => buildDefaultMap());
  const [valoresPreventiva, setValoresPreventiva] = useState(() => buildDefaultMap());

  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");

      try {
        const snap = await getDoc(doc(db, COLLECTION_VALORES, usuario.grupoId));
        if (!snap.exists()) return;

        const data = snap.data() || {};
        if (data.instalacaoPorTipo && typeof data.instalacaoPorTipo === "object") {
          setValoresInstalacao((prev) => ({ ...prev, ...data.instalacaoPorTipo }));
        }
        if (data.preventivaPorTipo && typeof data.preventivaPorTipo === "object") {
          setValoresPreventiva((prev) => ({ ...prev, ...data.preventivaPorTipo }));
        }
      } catch (e) {
        console.error("Erro ao carregar valores de serviços:", e);
        setErro("Erro ao carregar valores de instalação e preventiva.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId]);

  const handleSalvar = async () => {
    if (!usuario?.grupoId) {
      setErro("Usuário sem grupoId. Faça login novamente.");
      return;
    }

    setErro("");
    setSucesso("");
    setSalvando(true);
    try {
      await setDoc(
        doc(db, COLLECTION_VALORES, usuario.grupoId),
        {
          grupoId: usuario.grupoId,
          instalacaoPorTipo: valoresInstalacao,
          preventivaPorTipo: valoresPreventiva,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSucesso("Valores salvos com sucesso.");
    } catch (e) {
      console.error("Erro ao salvar valores de serviços:", e);
      setErro("Erro ao salvar valores de instalação e preventiva.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Cadastros"
      subtitulo="Financeiro / Cadastros / Valores de Serviços"
    >
      <section className="pc-card pc-form-card">
        <header className="pc-form-header">
          <h3>Valores por tipo de equipamento</h3>
          <p>Configure os valores unitários usados no cálculo das propostas de instalação e preventiva.</p>
        </header>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        {carregando ? (
          <p style={{ padding: 10 }}>Carregando valores...</p>
        ) : (
          <div className="pc-form" style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div className="pc-form-group pc-form-group-full">
                <strong>Instalação (R$ unitário)</strong>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 8 }}>
                  {TIPOS_EQUIPAMENTO.map((tipo) => (
                    <div
                      key={`inst-${tipo}`}
                      style={{ border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, padding: "8px 10px", background: "rgba(15,23,42,0.35)" }}
                    >
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, opacity: 0.85 }}>
                        <div>{tipo}</div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="pc-input"
                          value={valoresInstalacao[tipo] ?? 0}
                          onChange={(e) =>
                            setValoresInstalacao((prev) => ({
                              ...prev,
                              [tipo]: parseNumero(e.target.value),
                            }))
                          }
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pc-form-group pc-form-group-full">
                <strong>Preventiva (R$ unitário)</strong>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 8 }}>
                  {TIPOS_EQUIPAMENTO.map((tipo) => (
                    <div
                      key={`prev-${tipo}`}
                      style={{ border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, padding: "8px 10px", background: "rgba(15,23,42,0.35)" }}
                    >
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, opacity: 0.85 }}>
                        <div>{tipo}</div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="pc-input"
                          value={valoresPreventiva[tipo] ?? 0}
                          onChange={(e) =>
                            setValoresPreventiva((prev) => ({
                              ...prev,
                              [tipo]: parseNumero(e.target.value),
                            }))
                          }
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pc-form-actions" style={{ justifyContent: "flex-end" }}>
              <button type="button" className="pc-btn-primario" onClick={handleSalvar} disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar valores"}
              </button>
            </div>
          </div>
        )}
      </section>
    </LayoutDesktop>
  );
}
